import {
    fetchLiveCatalogStatus,
    fetchLiveCatalogStatusFromPubnub as fetchTheLiveCatalogStatusFromPubnub,
} from '@/redux/api/liveCatalogStatus';
import { getAuthToken, getBidderId } from './user';
import { getDeployment, getPubnubSubscribeKey } from './config';
import {
    LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
    LIVE_UPDATE_TIMESTAMP,
    LOAD_LIVE_CATALOG_STATUS_FAIL,
    LOAD_LIVE_CATALOG_STATUS_REQUEST,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
} from './actions';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { difference } from 'lodash';
import { getCatalog } from './catalog';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

const REDUX_STORE_TIME = ms('30m');

interface DefaultState {
    readonly loaded: {
        [catalogId: number]: number;
    };
    readonly loading: number[];
}

/* reducer */
const DEFAULT_STATE: DefaultState = {
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let loaded: DefaultState['loaded'];
    let loading: DefaultState['loading'];
    let time: number;

    switch (action.type) {
        case LOAD_LIVE_CATALOG_STATUS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_LIVE_CATALOG_STATUS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;
            loaded[action.meta.catalogId] = time;
            return {
                ...state,
                loaded,
                loading: difference(loading, [action.payload.catalogId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.liveCatalogStatus;
const idSelector = (state: GlobalState, id) => id;

export const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

export const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getLoadTimeForLiveCatalogStatus = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

export const isLiveCatalogStatusLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

const shouldFetchLiveCatalogStatus = (state, catalogId) => {
    if (!catalogId) {
        return false;
    }
    const loaded = getLoadTimeForLiveCatalogStatus(state, catalogId);
    if (loaded) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isLiveCatalogStatusLoading(state, catalogId);
    return !loading;
};

/* ACTION CREATORS */
export const loadLiveCatalogStatus = (catalogId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        let bidderId = getBidderId(state);
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const { sellerId } = getCatalog(state, catalogId);

        dispatch({
            payload: catalogId,
            type: LOAD_LIVE_CATALOG_STATUS_REQUEST,
        });

        /**
         * Get the current auction state from auction-engine
         */
        const response = await fetchLiveCatalogStatus({ authToken, catalogId, deployment, sellerId });

        if (response.error === true) {
            dispatch({
                meta: { actionTime: Date.now(), bidderId, catalogId },
                payload: {
                    status: 'notLoaded',
                },
                type: LOAD_LIVE_CATALOG_STATUS_SUCCESS,
            });
        } else {
            dispatch({
                meta: { actionTime: Date.now(), bidderId, catalogId },
                payload: response.data,
                type: LOAD_LIVE_CATALOG_STATUS_SUCCESS,
            });
            if (response?.data?.status === 'live') {
                dispatch({
                    payload: {
                        catalogId,
                        timestamp: Date.now(),
                    },
                    type: LIVE_UPDATE_TIMESTAMP,
                });
            }
        }
    } catch (error) {
        dispatch({
            error: true, // TODO: this isn't used
            meta: { catalogId },
            payload: error,
            type: LOAD_LIVE_CATALOG_STATUS_FAIL,
        });
    }
};

export const fetchLiveCatalogStatusFromPubnub = (catalogId: number) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const subscribeKey = getPubnubSubscribeKey(state);
        const channel = `catalog.${catalogId}`;
        const { occupancy: bidderCount } = await fetchTheLiveCatalogStatusFromPubnub({
            channel,
            subscribeKey,
        });

        dispatch({
            payload: { bidderCount, catalogId },
            type: LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
        });
    } catch (error) {
        // do nothing
    }
};

export const fetchLiveCatalogStatusIfNeeded = (catalogId: number, force: boolean = false) => async (
    dispatch: Function,
    getState: Function
) => {
    if (force || shouldFetchLiveCatalogStatus(getState(), catalogId)) {
        await dispatch(loadLiveCatalogStatus(catalogId));
    }
    return Promise.resolve();
};
